/*
Interesting dimensions to keep in mind :
CARD_HEIGHT * CARD_IN_PLAY_SCALE / GRID_SIZE = 10
CARD_WIDTH * CARD_IN_PLAY_SCALE / GRID_SIZE = 7.16

But we set default player scale to 1.2, so :
CARD_HEIGHT * CARD_IN_PLAY_SCALE * DEFAULT_PLAYER_SCALE / GRID_SIZE = 12
CARD_WIDTH * CARD_IN_PLAY_SCALE DEFAULT_PLAYER_SCALE / GRID_SIZE = 7.16

So a card in play take exactly 12 grid cells in height, and ~8.5 cells in width (9 with some spacing ).
This allows to align nicely the cards side by side on the y axis,
and have a gap between them in the x axis.
 */

/**
 * Base constants
 */

export const BLACK = 'rgb(0,0,0)'
export const WHITE = 'rgb(255,255,255)'

export const OPAQUE_ALPHA = 255
export const TRANSPARENT_ALPHA = 0

export const HIGHLIGHT_YELLOW = 'rgb(255,200,80)'

export const HD_WIDTH = 1920
export const HD_HEIGHT = 1080

/**
 * Card in play
 */

export const CARD_HEIGHT = 500
export const CARD_WIDTH = 358

export const CARD_IN_PLAY_BASE_SCALE = 0.2
export const CARD_IN_STACK_SCALE = 0.15
export const CARD_IN_HAND_SCALE = 0.4
export const DEFAULT_PLAYER_SCALE = 1.2

export const CARD_DRAGGING_ALPHA = 0.7

export const CARD_OUTLINE_THICKNESS = 2
export const CARD_OUTLINE_COLOR_HOVER = HIGHLIGHT_YELLOW
export const CARD_OUTLINE_COLOR_SELECTED = 'rgb(140,70,255)'
export const CARD_OUTLINE_COLOR_INDIRECT_HOVER = 'rgb(255, 228, 169)'
export const ACTION_DECLARATION_OUTLINE_COLOR = 'rgb(70,150,255)'

export const CARD_GLOW_COLOR = 'rgb(180,90,40)'
// A game with the rules engine : a candidate of the choice under way ( the target declaration's
// blue ), and the card that choice is about
export const CARD_GLOW_CANDIDATE_COLOR = ACTION_DECLARATION_OUTLINE_COLOR
// White, apart from the hover outline's yellow : the card a choice is about
export const CARD_GLOW_STAGED_COLOR = 'rgb(255,255,255)'
export const CARD_IN_HAND_GLOW_INNER_STRENGTH = 0
export const CARD_IN_HAND_GLOW_OUTER_STRENGTH = 2
export const CARD_IN_PLAY_GLOW_INNER_STRENGTH = 0
export const CARD_IN_PLAY_GLOW_OUTER_STRENGTH = 3
// A minion that can act : the usable orange, thinner ( a card to play glows full )
export const CARD_IN_PLAY_GLOW_ACTOR_OUTER_STRENGTH = 1.5

export const CARD_PING_DURATION = 2000 // Total duration of the ping animation in ms
export const CARD_PING_NB_BEATS = 4 // Number of ping beats
export const CARD_PING_COLOR = 'rgb(152,32,56)'

// Floating cost indicator ( automatic cost payment ) : the cost rises above the
// card and fades out.
export const CARD_COST_FX_DURATION = 1200 // Total duration of the rise/fade in ms
const CARD_COST_FX_TEXT_STYLE_BASE = {
    fontStyle: 'bold',
    fontSize: '24px',
    stroke: 'black',
    strokeThickness: 4,
}
export const BLOOD_COST_FX_TEXT_STYLE = { ...CARD_COST_FX_TEXT_STYLE_BASE, color: 'rgb(220,40,40)' }
export const POOL_COST_FX_TEXT_STYLE = { ...CARD_COST_FX_TEXT_STYLE_BASE, color: 'white' }

/**
 * Play Area Dimensions
 */

export const GRID_SIZE = 10 // 10 px

export const RIGHT_COLUMN_WIDTH = 342 // column 338px + border 4px = 342px
export const WORLD_WIDTH = HD_WIDTH - RIGHT_COLUMN_WIDTH // 1578 px
export const WORLD_HEIGHT = HD_HEIGHT // 1080 px

export const PLAY_AREA_WIDTH = 64 * GRID_SIZE //640px
export const PLAY_AREA_X = (WORLD_WIDTH - PLAY_AREA_WIDTH) / 2 // 469 px

export const PLAYER_BAR_HEIGHT = 3 * GRID_SIZE // 30px
export const CONTROLLED_ZONE_HEIGHT = 39 * GRID_SIZE // 370px
export const TORPOR_ZONE_HEIGHT = 10 * GRID_SIZE // 100px
export const TORPOR_ZONE_Y = PLAYER_BAR_HEIGHT + CONTROLLED_ZONE_HEIGHT // 400px
export const TORPOR_ZONE_BOTTOM = TORPOR_ZONE_Y + TORPOR_ZONE_HEIGHT // 500px
export const CARD_STACKS_Y = TORPOR_ZONE_BOTTOM + GRID_SIZE // 510px
export const CARD_STACKS_HEIGHT = CARD_IN_STACK_SCALE * CARD_WIDTH + 10 // 64px
export const PLAY_AREA_HEIGHT = CARD_STACKS_Y + CARD_STACKS_HEIGHT // 574px
export const UNCONTROLLED_WIDTH = GRID_SIZE * 8 * 4 - GRID_SIZE // 310px

export const HORIZONTAL_SEPARATOR_DEFAULT_Y = TORPOR_ZONE_Y
export const HORIZONTAL_SEPARATOR_MIN_Y = PLAYER_BAR_HEIGHT + 20 * GRID_SIZE
export const HORIZONTAL_SEPARATOR_MAX_Y = TORPOR_ZONE_Y

export const VERTICAL_SEPARATOR_DEFAULT_X = PLAY_AREA_WIDTH - UNCONTROLLED_WIDTH
export const VERTICAL_SEPARATOR_MIN_X = 8 * GRID_SIZE
export const VERTICAL_SEPARATOR_MAX_X = PLAY_AREA_WIDTH - VERTICAL_SEPARATOR_MIN_X

// The hand at the very bottom of the world : its cards ( CARD_IN_HAND_SCALE ) fill its height
export const HAND_HEIGHT = CARD_HEIGHT * CARD_IN_HAND_SCALE // 200px
export const HAND_WIDTH = WORLD_WIDTH * 0.99
export const HAND_X = (WORLD_WIDTH - HAND_WIDTH) / 2
export const HAND_Y = WORLD_HEIGHT - HAND_HEIGHT // 880px

// The central player's play area right above the hand, leaving the room above it to the top area
export const HAND_GUTTER = 8
export const PLAY_AREA_Y = HAND_Y - HAND_GUTTER - PLAY_AREA_HEIGHT // 298px

export const TOP_AREA_WIDTH = PLAY_AREA_WIDTH
// Down to the central player's bar ( its name and pool, overlapped by the top area's margin )
export const TOP_AREA_HEIGHT = PLAY_AREA_Y + 20 // 318px
export const TOP_AREA_X = PLAY_AREA_X

export const OTHER_PLAYERS_HORIZONTAL_GUTTER = 12
export const OTHER_PLAYERS_VERTICAL_GUTTER = 5
export const OTHER_PLAYERS_SCALE = 0.72
export const RIGHT_PLAYERS_X = WORLD_WIDTH * 0.7 + OTHER_PLAYERS_HORIZONTAL_GUTTER
export const BOTTOM_PLAYERS_Y = WORLD_HEIGHT * 0.4 + OTHER_PLAYERS_VERTICAL_GUTTER

export const TWO_PLAYERS_HORIZONTAL_GUTTER = (WORLD_WIDTH - PLAY_AREA_WIDTH * 2) / 4

/**
 * Play Area Colors
 */

export const REGION_ALPHA = OPAQUE_ALPHA * 0.75
export const REGION_BACKGROUND_COLOR = `rgba(128,128,128,${REGION_ALPHA / 255})`
export const REGION_STACK_SIZE_BACKGROUND_COLOR = 'rgb(150, 150, 150)'

// Matches the CSS right-column resize handles ( $lighter-teal #3a7d7d )
export const SEPARATOR_COLOR = 'rgb(58,125,125)'
export const SEPARATOR_THICKNESS = 8

export const PLAYER_COLORS = {
    purple: 'rgb(60,20,95)', // dark purple
    green: 'rgb(15,70,35)', // dark emerald green
    crimson: 'rgb(105,20,20)', // dark crimson
    azure: 'rgb(2,75,115)', // dark azure blue
    amber: 'rgb(125,58,6)', // dark amber
}

export const ORDERED_PLAYER_COLORS = [
    PLAYER_COLORS.purple,
    PLAYER_COLORS.azure,
    PLAYER_COLORS.crimson,
    PLAYER_COLORS.green,
    PLAYER_COLORS.amber,
]

/**
 * Selection Area
 */

export const SELECTION_AREA_LINE_THICKNESS = 1
export const SELECTION_AREA_COLOR = HIGHLIGHT_YELLOW

/**
 * Card Group
 */

export const CARD_GROUP_OUTLINE_COLOR = 'rgb(10,165,140)'
export const CARD_GROUP_BOUNDING_BOX_THICKNESS = 1
export const CARD_GROUP_BOUNDING_BOX_COLOR = 'rgb(75,75,75)'
export const CARD_GROUP_ICON_WIDTH = 40
export const CARD_GROUP_ICON_HEIGHT = 50
export const CARD_GROUP_ICON_MARGIN = 5
export const CARD_GROUP_BACKGROUND_COLOR = 'rgba(255,255,255,0.5)'

/**
 * Counter
 */

export const COUNTER_RADIUS = 12
export const COUNTER_OUTLINE_THICKNESS = 1
export const COUNTER_HOVER_OFFSET_MULTIPLIER = 1.6
export const COUNTER_OUTLINE_COLOR = BLACK
export const COUNTER_TEXT_STYLE = {
    color: 'black',
    fontStyle: 'bold',
    fontSize: '18px',
}
export const BLOOD_COUNTER_FILL_COLOR = 'rgb(150,0,0)'
export const GREEN_COUNTER_FILL_COLOR = 'rgb(0,150,0)'
export const ORANGE_COUNTER_FILL_COLOR = 'rgb(200,150,0)'

/**
 * Button
 */

export const BUTTON_BORDER_WIDTH = 1
export const BUTTON_BORDER_COLOR = 'rgb(26,26,26)'
export const BUTTON_BACKGROUND_COLOR = 'rgb(21,47,47)'
export const BUTTON_TEXT_STYLE = {
    color: 'white',
    fontStyle: 'bold',
    fontSize: '14px',
}

export const OVERLAY_BUTTON_SIZE = COUNTER_RADIUS * COUNTER_HOVER_OFFSET_MULTIPLIER

/**
 * Markers
 */

export const MARKER_MARGIN_TOP = 15
export const MARKER_HEIGHT = 20
export const MARKER_WIDTH_PER_CHAR = 8
export const MARKER_PADDING = 10

export const MARKERS_FILL_COLOR = 'rgba(118,74,43,0.9)'
export const MARKERS_TEXT_STYLE = {
    color: 'black',
    fontStyle: 'bold',
    fontSize: '13px',
}

// A counted marker of the rules engine ( hostage, nightmare, torture, a lasting effect's
// counters ) : a round chip, its count on it, one color per kind, its name on hover
export const CHIP_RADIUS = 10
export const CHIP_GAP = 2
export const CHIP_TEXT_STYLE = {
    color: 'black',
    fontStyle: 'bold',
    fontSize: '14px',
}
export const HOSTAGE_CHIP_FILL_COLOR = 'rgb(90,160,230)'
export const NIGHTMARE_CHIP_FILL_COLOR = 'rgb(175,120,230)'
export const TORTURE_CHIP_FILL_COLOR = 'rgb(230,130,175)'
export const EFFECT_CHIP_FILL_COLOR = 'rgb(200,200,200)'

/**
 * Wield Card Stack
 */

export const WIELD_X = 0
export const WIELD_Y = 0
export const WIELD_CARD_STACK_HEIGHT = 270
export const WIELD_ACTIONS_WIDTH = 190
export const WIELD_BORDER_COLOR = BLACK
export const WIELD_BACKGROUND_COLOR = REGION_BACKGROUND_COLOR

export const WIELD_CARD_SCALE = 0.45
export const WIELD_CARD_DISPLAY_WIDTH = WIELD_CARD_SCALE * CARD_WIDTH + 8
export const WIELD_CARDS_OFFSET = 15
export const WIELD_INDICATOR_WIDTH = 25

export const WIELD_SCROLLBAR_HEIGHT = 15
export const WIELD_SCROLLBAR_COLOR = 'rgb(60,60,60)'
export const WIELD_SCROLLBAR_ALPHA = 0.6

/**
 * Arrows
 */

export const ARROW_HEAD_WIDTH = 20
export const ARROW_HEAD_HEIGHT = 20
export const ARROW_COLOR = 'rgb(135,45,60)'

/**
 * Alignment guides
 */

export const ALIGNMENT_GUIDE_THRESHOLD = 1.5 * GRID_SIZE // Distance threshold for showing alignment guide
export const ALIGNMENT_GUIDE_COLOR = 'rgb(50,200,50)'
export const ALIGNMENT_GUIDE_WIDTH = 1
export const ALIGNMENT_GUIDE_OVERSHOOT = 20

/**
 * Misc
 */

export const DRAG_DISTANCE_THRESHOLD = 5
export const CARD_LOG_PLACEHOLDER = '[CARD]'

/**
 * Free Table
 */

export const FREE_TABLE_WIDTH = 8000
export const FREE_TABLE_HEIGHT = 6500
// Radius of the circle seats are laid out on ( see freeTableLayout.ts ). One
// value per player count rather than a single shared radius : each seating
// shape insets its edge midpoints towards the center by a different amount
// ( a square's by cos(45deg), a triangle's by cos(60deg), face-to-face not at
// all ), so a single radius put 2 players too far apart and 3 too close.
// Tuned for a roughly equal felt seat-to-center distance across counts.
export const FREE_TABLE_LAYOUT_RADIUS: Record<number, number> = {
    1: 900,
    2: 350,
    3: 1000,
    4: 800,
    5: 800,
}
// Distance from the pentagon edge to a PlayerWidget's initial anchor position
export const FREE_TABLE_PERIMETER_MARGIN = 0
// The PlayerWidget is a disc ( see PlayerWidget.vue ) : its content stacks in a
// single column with a couple of two-column rows, and it keeps its own
// orientation regardless of the seat, so a circle reads the same all around.
export const FREE_TABLE_WIDGET_RADIUS = 118
// Gap between the edge of a PlayerWidget's disc and its initial face-down crypt cards
export const FREE_TABLE_CRYPT_CARDS_MARGIN = 20
// Horizontal spacing between initial face-down crypt cards, in GRID_SIZE units
export const FREE_TABLE_CRYPT_CARDS_SPACING = 8
// Screen-space margin ( same pixel space as HAND_HEIGHT / TOP_AREA_WIDTH )
// kept clear above the seats by the default camera fit ( see
// resetFreeTableCamera ), so seats don't land flush against the top edge
export const FREE_TABLE_TOP_MARGIN = 10
export const FREE_TABLE_BOTTOM_MARGIN = 75
